import { Button, Dialog, DialogActions } from "@mui/material";
import { CreateTestCaseFromChatDocument, FormField, TestCaseCorrectedValueInput } from "generated/graphql";
import { runGqlMutation } from "gqlClient";
import { useSnackbar } from "notistack";
import { SetStateAction, useContext, useEffect, useState } from "react";
import { extractGQLError } from "shared/PatternGraphQLError";
import { ValueGroup } from "./FormField/ValueGroup";
import { FieldCellValueUI, isNotUndefined } from "Backend";
import { P } from "theme/typography";
import { TestCaseLabelsAndDescription } from "routes/lens-editor/ai-chats/TestCaseLabelsAndDescription";
import { FormFieldContext } from "contexts/FormFieldContext";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { fetchAiChatMessages, selectLensEditorAiChatWithMessages } from "redux/slices/lensEditor";

export function TestCaseDialog({
  open,
  close,
  lensId,
  aiChatId,
  formField,
}: {
  open: boolean;
  close: () => void;
  lensId: number;
  aiChatId: string;
  formField: FormField;
}) {
  const dispatch = useAppDispatch();
  const [description, setDescription] = useState("");
  const ffContext = useContext(FormFieldContext);
  const valueGroupsForChat = ffContext.valueGroups.filter((vg) => vg.source?.sourceAiChatId === aiChatId);
  const aiChat = useAppSelector(selectLensEditorAiChatWithMessages);

  const [corrections, setCorrections] = useState<Map<number, FieldCellValueUI[]>>(
    valueGroupsForChat.reduce((state, vg) => {
      const vgState = formField.cells
        .map((cell) => {
          const value = vg.values.find((v) => v.cellId === cell.id)?.value;
          return value !== undefined
            ? {
                cellId: cell.id,
                value,
                valueGroupIndex: vg.id,
              }
            : undefined;
        })
        .filter(isNotUndefined);
      state.set(vg.serialId, vgState);
      return state;
    }, new Map())
  );
  const { enqueueSnackbar } = useSnackbar();

  const onValueGroupChange = (valueGroupId: number, action: SetStateAction<FieldCellValueUI[]>) => {
    const currentStateFragment = corrections.get(valueGroupId);
    if (!currentStateFragment) return;
    const nextStateFragment = typeof action === "function" ? action(currentStateFragment) : action;
    const nextState = corrections.set(valueGroupId, nextStateFragment);
    setCorrections(nextState);
    return nextStateFragment;
  };

  const getCorrectedValues = (): TestCaseCorrectedValueInput[][] => {
    return Array.from(corrections.values()).map((fieldCellValues) => {
      return fieldCellValues.map((vg) => ({ cellId: vg.cellId, value: vg.value }));
    });
  };

  const add = () => {
    runGqlMutation(CreateTestCaseFromChatDocument, {
      lensId,
      aiChatId,
      description,
      correctedValues: getCorrectedValues(),
    })
      .then((_res) => {
        enqueueSnackbar("Test case created", { variant: "info" });
        close();
      })
      .catch((err) => {
        const gql = extractGQLError(err);
        enqueueSnackbar(`Failed to create test case: ${gql?.message ?? "unknown"}`, { variant: "error" });
      });
  };

  useEffect(() => {
    if (aiChat === undefined || aiChat.chat.id !== aiChatId) {
      dispatch(fetchAiChatMessages({ chatId: aiChatId, lensId }));
    }
  }, [aiChatId]);

  const pageNumbers = (aiChat?.messages ?? []).flatMap((aiChat) => {
    if (aiChat.format.__typename === "Blocks") {
      return aiChat.format.pageNumbers;
    } else if (aiChat.format.__typename === "PageImage") {
      return [aiChat.format.pageNumber];
    } else {
      return [];
    }
  });

  const isPartOfResponseGroup = valueGroupsForChat.length > 1;

  return (
    <Dialog open={open} fullWidth maxWidth="sm" classes={{ container: "justify-start" }}>
      <div className="p-4">
        <header className="sticky z-10 top-0 bg-white py-2">
          <div className="grid gap-2">
            <p className="text-xl font-semibold">Add Test Case</p>
          </div>
        </header>
        <div className="grid grid-cols-2 pb-2">
          <div className="flex items-center text-xs gap-x-2">
            <P variant="xsSemiBold">Chat</P>
            <p>{aiChatId}</p>
          </div>
          {pageNumbers.length > 0 && (
            <div className="flex items-center text-xs gap-x-2">
              <P variant="xsSemiBold">Pages</P>
              <p>{pageNumbers.join(", ")}</p>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-4 max-h-screen overflow-auto">
          <TestCaseLabelsAndDescription description={description} onChange={setDescription} />
          {isPartOfResponseGroup && (
            <div>
              <p className="text-xs text-gray-500 italic">
                This response is part of a group of responses ({valueGroupsForChat.length}) created by the same AI chat.
              </p>
              <p className="text-xs text-gray-500 italic">Please correct as many as you can.</p>
            </div>
          )}
          <P variant="xsSemiBold">Corrected Cell Values</P>
          <div className="flex flex-col gap-4">
            {valueGroupsForChat.map((vg, i) => {
              // We don't actually want to use state for this but the ValueGroup component expects a state handle,
              // so creating some that we can pass to it, and updating what we care about (the whole list)
              const initialState = corrections.get(vg.serialId);
              if (!initialState) return;

              const [stateFragment, setStateFragment] = useState<FieldCellValueUI[]>(initialState);
              const handler = (current: SetStateAction<FieldCellValueUI[]>) => {
                const nextFragment = onValueGroupChange(vg.serialId, current);
                nextFragment && setStateFragment(nextFragment);
              };
              return (
                <>
                  {isPartOfResponseGroup && <h6 className="font-semibold">Response {i + 1}</h6>}
                  <ValueGroup
                    key={i}
                    compact
                    cells={formField.cells}
                    stateHandle={[stateFragment, handler]}
                    displayEmptyOption={true}
                  />
                </>
              );
            })}
          </div>
        </div>
      </div>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button variant="contained" onClick={add}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
